// src/components/AddProject.jsx
import React, { useState } from "react";
import { db, storage } from "../firebase";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import "./AddProject.css";

const AddProject = () => {
  const navigate = useNavigate();
  const CATEGORY_OPTIONS = {
    urbanism: ["City Scale", "Residential", "Entertainment", "Infrastructure"],
    architecture: [
      "Administrative",
      "Commercial",
      "Hospitality",
      "Residential",
      "Educational",
      "Cultural",
      "Entertainment",
      "Medical",
      "Religious",
      "Restoration",
      "Infrastructure",
    ],
    "interior design": ["Residential", "Commercial", "Cultural"],
  };
  const [saving, setSaving] = useState(false);
  const [activeUploads, setActiveUploads] = useState(0);
  const [project, setProject] = useState({
    id: "",
    title: "",
    caption: "",
    description: "",
    order: "",
    image: "",
    category: [],
    categories: {},
    info: {
      client: "",
      year: "",
      status: "",
      location: "",
      program: "",
      scale: "",
    },
    map: {
      lat: "",
      long: "",
    },
    details: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parts = name.split(".");

    if (parts.length === 2) {
      setProject((prev) => ({
        ...prev,
        [parts[0]]: {
          ...prev[parts[0]],
          [parts[1]]: value,
        },
      }));
    } else {
      setProject((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handleMainCategoryChange = (category) => {
    setProject((prev) => {
      const exists = prev.category.includes(category);

      if (exists) {
        const updatedCategories = { ...prev.categories };
        delete updatedCategories[category];

        return {
          ...prev,
          category: prev.category.filter((c) => c !== category),
          categories: updatedCategories,
        };
      }

      return {
        ...prev,
        category: [...prev.category, category],
        categories: {
          ...prev.categories,
          [category]: [],
        },
      };
    });
  };
  const handleSubCategoryChange = (mainCategory, subCategory) => {
    setProject((prev) => {
      const current = prev.categories[mainCategory] || [];
      const exists = current.includes(subCategory);

      return {
        ...prev,
        categories: {
          ...prev.categories,
          [mainCategory]: exists
            ? current.filter((item) => item !== subCategory)
            : [...current, subCategory],
        },
      };
    });
  };
  const DETAIL_TYPES = [
    "slogan",
    "parallel",
    "gallery",
    "slider",
    "video",
    "section",
    "image",
    "double_img",
    "render3d",
    "video_only",
    "vr",
  ];

  const addDetail = () => {
    setProject((prev) => ({
      ...prev,
      details: [...prev.details, { type: "", order: prev.details.length + 1 }],
    }));
  };

  const updateDetail = (index, key, value) => {
    setProject((prev) => {
      const updated = [...prev.details];
      updated[index] = {
        ...updated[index],
        [key]: value,
      };
      return { ...prev, details: updated };
    });
  };

  const addGalleryImage = (detailIndex) => {
    setProject((prev) => {
      const updated = [...prev.details];
      updated[detailIndex].images = [
        ...(updated[detailIndex].images || []),
        { image: "", caption: "" },
      ];
      return { ...prev, details: updated };
    });
  };

  const updateGalleryImage = (detailIndex, imgIndex, key, value) => {
    setProject((prev) => {
      const updated = [...prev.details];
      updated[detailIndex].images[imgIndex][key] = value;
      return { ...prev, details: updated };
    });
  };

  const uploadImage = async (file) => {
    const imageRef = ref(storage, `projects/${project.id}/${Date.now()}`);
    await uploadBytes(imageRef, file);
    return await getDownloadURL(imageRef);
  };
  const uploadFile = async (file, folder = "assets") => {
    const fileRef = ref(
      storage,
      `projects/${project.id}/${folder}/${Date.now()}-${file.name}`
    );

    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };
  const handleDetailFileUpload = async (index, field, file, folder) => {
    if (!file) return;

    setActiveUploads((c) => c + 1);

    try {
      const url = await uploadFile(file, folder);

      setProject((prev) => {
        const updated = [...prev.details];
        updated[index] = {
          ...updated[index],
          [field]: url,
        };
        return { ...prev, details: updated };
      });
    } catch (err) {
      console.error("Upload failed:", err);
      alert("One of the uploads failed");
    } finally {
      setActiveUploads((c) => c - 1);
    }
  };

  const handleGalleryImageUpload = async (detailIndex, imgIndex, file) => {
    const url = await uploadFile(file, "images");

    setProject((prev) => {
      const updated = [...prev.details];
      updated[detailIndex].images[imgIndex].image = url;
      return { ...prev, details: updated };
    });
  };
  const handleSubmit = async (e) => {
    if (activeUploads > 0) {
      alert("Please wait for uploads to finish");
      return;
    }

    e.preventDefault();

    if (saving) return;
    setSaving(true);

    if (!project.id.trim()) {
      alert("Project ID is required");
      setSaving(false);
      return;
    }

    try {
      console.log("Submitting project...", project);

      // Upload main project image
      const fileInput = document.getElementById("projectImage");
      const file = fileInput?.files?.[0];

      let imageUrl = "";
      if (file) {
        imageUrl = await uploadImage(file);
      }

      // Clean categories
      const cleanedCategories = Object.fromEntries(
        Object.entries(project.categories).filter(
          ([_, value]) => Array.isArray(value) && value.length > 0
        )
      );

      // Clean details
      const cleanedDetails = project.details
        .filter((d) => d.type && d.order)
        .map((d) => {
          const clean = { ...d };
          Object.keys(clean).forEach((key) => {
            if (clean[key] === undefined) delete clean[key];
          });
          return clean;
        });

      // 🔥 FORMAT SCALE HERE (THIS IS THE ANSWER)
      const formattedScale = project.info.scale
        ? `${project.info.scale} m<sup>2</sup>`
        : "";

      // 🔥 SAVE TO FIRESTORE
      await setDoc(doc(db, "projects", project.id.trim()), {
        ...project,
        image: imageUrl,
        categories: cleanedCategories,
        details: cleanedDetails,
        info: {
          ...project.info,
          scale: formattedScale,
        },
      });

      console.log("Project saved successfully");
      navigate("/project");
    } catch (error) {
      console.error("SAVE FAILED:", error);
      alert("Save failed. Check console.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-project-container">
      <h2>Add New Project</h2>

      <form className="add-project-form" onSubmit={handleSubmit}>
        <input
          name="id"
          placeholder="Project ID"
          required
          onChange={handleChange}
        />
        <input
          name="title"
          placeholder="Title"
          required
          onChange={handleChange}
        />
        <input name="caption" placeholder="Caption" onChange={handleChange} />
        <textarea
          name="description"
          placeholder="Description"
          onChange={handleChange}
        />
        <input id="projectImage" type="file" accept="image/*" />
        <div className="add-project-section">
          <h4>Project Info</h4>
          <input
            name="info.client"
            placeholder="Client"
            onChange={handleChange}
          />
          <input name="info.year" placeholder="Year" onChange={handleChange} />
          <input
            name="info.status"
            placeholder="Status"
            onChange={handleChange}
          />
          <input
            name="info.location"
            placeholder="Location"
            onChange={handleChange}
          />
          <input
            name="info.program"
            placeholder="Program"
            onChange={handleChange}
          />
          <input
            type="number"
            placeholder="Scale (m²)"
            value={project.info.scale}
            onChange={(e) =>
              setProject((prev) => ({
                ...prev,
                info: {
                  ...prev.info,
                  scale: e.target.value,
                },
              }))
            }
          />
        </div>

        <div className="add-project-section">
          <h4>Location</h4>
          <input
            name="map.lat"
            placeholder="Latitude"
            onChange={handleChange}
          />
          <input
            name="map.long"
            placeholder="Longitude"
            onChange={handleChange}
          />
        </div>

        <div className="add-project-section categories-section">
          <h4>Categories</h4>

          {Object.keys(CATEGORY_OPTIONS).map((mainCategory) => {
            const isActive = project.category.includes(mainCategory);

            return (
              <div
                key={mainCategory}
                className={`main-category ${isActive ? "active" : ""}`}
              >
                {/* Main category */}
                <label className="main-category-label">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={() => handleMainCategoryChange(mainCategory)}
                  />
                  {mainCategory}
                </label>

                {/* Subcategories */}
                {isActive && (
                  <div className="subcategories">
                    {CATEGORY_OPTIONS[mainCategory].map((sub) => (
                      <label key={sub} className="subcategory-item">
                        <input
                          type="checkbox"
                          checked={
                            project.categories[mainCategory]?.includes(sub) ||
                            false
                          }
                          onChange={() =>
                            handleSubCategoryChange(mainCategory, sub)
                          }
                        />
                        {sub}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="add-project-section details-section">
          <h4>Details</h4>

          {project.details.map((detail, index) => (
            <div key={index} className="detail-box">
              {/* Type */}
              <select
                value={detail.type}
                onChange={(e) => updateDetail(index, "type", e.target.value)}
              >
                <option value="">Select type</option>
                {DETAIL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>

              {/* Order */}
              <input
                type="number"
                placeholder="Order"
                value={detail.order}
                onChange={(e) =>
                  updateDetail(index, "order", Number(e.target.value))
                }
              />

              {/* ===== TYPE-SPECIFIC FIELDS ===== */}

              {/* SLOGAN */}
              {detail.type === "slogan" && (
                <input
                  placeholder="Slogan text"
                  onChange={(e) => updateDetail(index, "data", e.target.value)}
                />
              )}

              {/* PARALLEL */}
              {detail.type === "parallel" && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleDetailFileUpload(
                        index,
                        "image",
                        e.target.files[0],
                        "images"
                      )
                    }
                  />

                  <input
                    placeholder="Caption"
                    onChange={(e) =>
                      updateDetail(index, "caption", e.target.value)
                    }
                  />
                  <label>
                    Vertical layout
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        updateDetail(index, "style2", e.target.checked)
                      }
                    />
                  </label>
                </>
              )}

              {/* GALLERY */}
              {detail.type === "gallery" && (
                <>
                  <input
                    placeholder="Gallery title"
                    onChange={(e) =>
                      updateDetail(index, "title", e.target.value)
                    }
                  />
                  <input
                    placeholder="Gallery description"
                    onChange={(e) =>
                      updateDetail(index, "description", e.target.value)
                    }
                  />
                  <label>
                    4 images per row
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        updateDetail(index, "extend", e.target.checked)
                      }
                    />
                  </label>

                  {(detail.images || []).map((img, imgIndex) => (
                    <div key={imgIndex}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleGalleryImageUpload(
                            index,
                            imgIndex,
                            e.target.files[0]
                          )
                        }
                      />

                      <input
                        placeholder="Caption"
                        onChange={(e) =>
                          updateGalleryImage(
                            index,
                            imgIndex,
                            "caption",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  ))}

                  <button type="button" onClick={() => addGalleryImage(index)}>
                    + Add Image
                  </button>
                </>
              )}

              {/* SLIDER */}
              {detail.type === "slider" && (
                <>
                  <input
                    placeholder="Header"
                    onChange={(e) =>
                      updateDetail(index, "header", e.target.value)
                    }
                  />
                  {(detail.images || []).map((img, imgIndex) => (
                    <div key={imgIndex}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleGalleryImageUpload(
                            index,
                            imgIndex,
                            e.target.files[0]
                          )
                        }
                      />

                      <input
                        placeholder="Caption"
                        onChange={(e) =>
                          updateGalleryImage(
                            index,
                            imgIndex,
                            "caption",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  ))}
                  <button type="button" onClick={() => addGalleryImage(index)}>
                    + Add Slide
                  </button>
                </>
              )}

              {/* VIDEO */}
              {detail.type === "video" && (
                <>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) =>
                      handleDetailFileUpload(
                        index,
                        "video",
                        e.target.files[0],
                        "videos"
                      )
                    }
                  />

                  <input
                    placeholder="Caption"
                    onChange={(e) =>
                      updateDetail(index, "caption", e.target.value)
                    }
                  />
                </>
              )}

              {/* IMAGE */}
              {detail.type === "image" && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleDetailFileUpload(
                      index,
                      "image",
                      e.target.files[0],
                      "images"
                    )
                  }
                />
              )}

              {/* DOUBLE IMAGE */}
              {detail.type === "double_img" && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleDetailFileUpload(
                        index,
                        "limage",
                        e.target.files[0],
                        "images"
                      )
                    }
                  />

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleDetailFileUpload(
                        index,
                        "rimage",
                        e.target.files[0],
                        "images"
                      )
                    }
                  />
                </>
              )}

              {/* VIDEO ONLY */}
              {detail.type === "video_only" && (
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) =>
                    handleDetailFileUpload(
                      index,
                      "video",
                      e.target.files[0],
                      "videos"
                    )
                  }
                />
              )}

              {/* RENDER 3D */}
              {detail.type === "render3d" && (
                <input
                  type="file"
                  accept=".glb,.gltf,.fbx,.obj"
                  onChange={(e) =>
                    handleDetailFileUpload(
                      index,
                      "file",
                      e.target.files[0],
                      "3d"
                    )
                  }
                />
              )}

              {/* VR PANORAMA */}
              {detail.type === "vr" && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleDetailFileUpload(
                        index,
                        "image",
                        e.target.files[0],
                        "vr"
                      )
                    }
                  />
                  <input
                    placeholder="Caption (optional)"
                    onChange={(e) =>
                      updateDetail(index, "caption", e.target.value)
                    }
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                    📸 Upload a 360° equirectangular panorama image (2:1 aspect ratio recommended)
                  </p>
                </>
              )}

              {/* SECTION */}
              {detail.type === "section" && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleDetailFileUpload(
                        index,
                        "image",
                        e.target.files[0],
                        "images"
                      )
                    }
                  />

                  <textarea
                    placeholder="Text"
                    onChange={(e) =>
                      updateDetail(index, "text", e.target.value)
                    }
                  />
                  <select
                    onChange={(e) =>
                      updateDetail(index, "align", e.target.value)
                    }
                  >
                    <option value="">Align</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                  <input
                    placeholder="Width % (optional)"
                    type="number"
                    onChange={(e) =>
                      updateDetail(index, "width", e.target.value)
                    }
                  />
                </>
              )}
            </div>
          ))}

          <button type="button" onClick={addDetail}>
            + Add Detail Block
          </button>
        </div>

        <div className="add-project-actions">
          <button
            type="submit"
            className="add-project-save"
            disabled={saving || activeUploads > 0}
          >
            {activeUploads > 0
              ? `Uploading (${activeUploads})...`
              : saving
              ? "Saving..."
              : "Save Project"}
          </button>

          <button
            type="button"
            className="add-project-cancel"
            onClick={() => navigate("/project")}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProject;